import { hexToRGB } from '@repo-level/core-front';
import { device } from 'components/styles/MediaQuery';
import styled, { css, keyframes } from 'styled-components/macro';

export interface ScAnimatedPlaceholderProps {
  active?: boolean;
}

export const ScAnimatedPlaceholder = styled.div<ScAnimatedPlaceholderProps>`
  position: absolute;
  z-index: 2;
  left: 0px;
  right: 16px;
  top: 50%;
  transform: translate(0, -50%);
  transition: 0.3s;
  pointer-events: none;
  font-size: 16px;
  line-height: 28px;
  align-items: center;
  color: ${({ theme, active }) =>
    active ? theme.whiteColor : theme.grayColor1};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export interface ScInputProps {
  small?: boolean;
  animatedPlaceholder?: string;
  error: boolean;
  icon: boolean;
  whiteWhenFilled?: boolean;
  bold?: boolean;
}

export const ScInput = styled.input<ScInputProps>`
  position: relative;
  height: 100%;
  width: 100%;
  border-radius: 12px;
  padding: ${({ icon }) => (icon ? '0 16px 4px 52px' : '0 16px 4px')};
  background-color: transparent;
  z-index: 2;
  appearance: none;
  outline: none;
  border: none;
  font-size: 16px;
  line-height: 28px;
  font-weight: ${({ bold }) => (bold ? 700 : 400)};

  & ~ ${ScAnimatedPlaceholder} {
    left: 16px;
  }

  ${({ whiteWhenFilled }) =>
    whiteWhenFilled &&
    css`
      -webkit-text-fill-color: ${({ theme }) => theme.blackColor};
      caret-color: ${({ theme }) => theme.blackColor};
    `}

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:focus,
  &.filled {
    padding-top: ${({ animatedPlaceholder }) =>
      animatedPlaceholder ? '16px' : '0'};

    & ~ ${ScAnimatedPlaceholder} {
      font-size: 10px;
      line-height: 14px;
      top: 12px;
      font-weight: 400;
    }
  }

  &:focus {
    color: ${({ theme }) => theme.whiteColor};
  }

  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    border: ${({ theme }) => `2px solid ${theme.greenColor}`};
  }

  &:-webkit-autofill {
    border: ${({ theme, error }) =>
      `2px solid ${error ? theme.pinkColor : theme.greenColor}`};
    & ~ ${ScAnimatedPlaceholder} {
      left: 18px;
    }
  }

  &::placeholder {
    color: ${({ theme }) => theme.grayColor1};
    -webkit-text-fill-color: ${({ theme }) => theme.grayColor1};
  }

  &:disabled {
    color: ${({ theme }) => theme.grayColor6};
    -webkit-text-fill-color: ${({ theme }) => theme.grayColor6};
  }

  &::-webkit-search-decoration,
  &::-webkit-search-cancel-button,
  &::-webkit-search-results-button,
  &::-webkit-search-results-decoration {
    display: none;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }

  &:hover {
    &::placeholder {
      color: ${({ theme }) => theme.whiteColor};
      -webkit-text-fill-color: ${({ theme }) => theme.whiteColor};
    }
  }
`;

export const ScInputIcon = styled.img`
  position: absolute;
  top: 8px;
  left: 7px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
`;

export const ScInputInternalLabel = styled.span`
  position: absolute;
  top: 8px;
  right: 16px;
  border-radius: 50%;
  top: 50%;
  transform: translateY(-50%);
  text-align: right;
  font-size: 16px;
  line-height: 28px;
`;

export interface ScSelectMainProps {
  small?: boolean;
  withBorderArrow?: boolean;
  up?: boolean;
  maxHeight?: number | 'none';
  paddingTop?: number;
  withBorderMode?: boolean;
  lightColoredDropMode?: boolean;
  dropBoxShadow?: boolean;
  lightBg?: boolean;
}

interface ScSelectBlockProps extends ScSelectMainProps {
  active?: boolean;
  error: boolean;
  disabled?: boolean;
  selected?: boolean;
  canReset?: boolean;
  lightColoredDropMode?: boolean;
  lightBg?: boolean;
  hasArrow?: boolean;
}

export const ScSelectBlock = styled.div<Pick<ScSelectBlockProps, 'active'>>`
  position: relative;
  z-index: ${({ active }) => (active ? '111' : '0')};
`;

export const ScSelectButton = styled.div<ScSelectBlockProps>`
  z-index: 1;
  position: relative;
  cursor: ${({ disabled, hasArrow }) =>
    !!disabled || !hasArrow ? 'default' : 'pointer'};
  padding: 10px 16px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 0;
  flex-wrap: nowrap;
  width: 100%;
  font-weight: 400;
  color: transparent;
  border: none;
  background-color: ${({ theme, lightBg }) =>
    lightBg ? theme.grayColor5 : theme.grayColor4};
  border-radius: 12px;

  ${({ small }) => {
    switch (false) {
      case small != null:
        return css`
          font-size: 14px;
        `;
      default:
        return css`
          font-size: 16px;
          line-height: 28px;
        `;
    }
  }}

  ${({ lightColoredDropMode, lightBg }) =>
    lightColoredDropMode &&
    css`
      background: ${({ theme }) =>
        lightBg ? theme.grayColor5 : theme.grayColor3};
      color: transparent;
      border: none;
      height: 48px;
    `}

  & > .border {
    position: absolute;
    z-index: 1;
    inset: 0;
    border-radius: 12px;
    padding: 2px;
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background: ${({ theme, active, error }) =>
        active
          ? theme.greenMarineGradient
          : error
            ? theme.pinkRougeGradient
            : 'transparent'};
    }
  }

  & > .content {
    width: 100%;
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    align-items: center;
    column-gap: 12px;
    z-index: 2;
    position: relative;
    overflow: hidden;

    & > .value {
      white-space: nowrap;
      color: ${({ theme, active, selected }) =>
        active || selected ? theme.whiteColor : theme.secondaryColor};
      overflow: hidden;

      & > .name {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        transition: color 0.3s ease;
        font-size: 16px;
      }

      &.filled {
        padding-top: 16px;
        color: ${({ theme }) => theme.whiteColor};
      }

      &.with-icon {
        display: flex;
        gap: 8px;

        & > .name {
          padding-left: 0;
        }

        img {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          object-fit: contain;
        }
      }
    }

    & > ${ScAnimatedPlaceholder} {
      &.filled {
        font-size: 10px;
        line-height: 14px;
        left: 0px;
        top: 6px;
      }
    }
  }

  & svg path {
    transition: fill-opacity 0.2s;
  }

  @media (hover: hover) {
    &:hover svg path {
      fill-opacity: 1;
    }
  }
  .arrow {
    position: relative;

    ${({ withBorderArrow, active }) =>
      withBorderArrow
        ? css`
            width: 24px;

            & > svg {
              position: absolute;
              left: 50%;
              top: 50%;
              transform: ${
                active
                  ? 'translate(-50%, -50%) rotate(180deg)'
                  : 'translate(-50%, -50%) rotate(0deg)'
              };
            }
          `
        : css`
            background: transparent;
            height: 100%;
            display: flex;
            align-items: center;

            & > svg {
              transform: ${active ? 'rotate(180deg)' : 'rotate(0deg)'};
            }
          `}

    & > button {
      position: absolute;
      left: 2px;
      right: 2px;
      top: 2px;
      bottom: 2px;
      border-radius: 3px;
      width: calc(100% - 4px);
      height: calc(100% - 4px);
      background-color: #6b6d7a;
      transition: all 0.2s ease;

      svg {
        width: 17px;
        height: 17px;
        object-fit: contain;

        path {
          fill: #e4e5f0;
        }
      }

      @media (hover: hover) {
        &:hover {
          background-color: ${({ theme }) => theme.secondaryColor};

          svg {
            transform: none;

            path {
              fill: ${({ theme }) => theme.whiteColor};
            }
          }
        }
      }
    }

    & > svg {
      transition: transform 0.3s;
    }
  }

  ${({ disabled, theme }) =>
    disabled &&
    css`
      pointer-events: none;
      cursor: not-allowed;

      & > .content {
        & > .value,
        & > .value.filled,
        & > ${ScAnimatedPlaceholder} {
          color: ${theme.grayColor6};
        }

        & > .arrow svg {
          path {
            fill: ${theme.grayColor6};
          }
        }
      }
    `}

  @media (hover: hover) {
    &:hover {
      .content {
        .value {
          color: ${({ theme, disabled }) =>
            disabled ? theme.grayColor6 : theme.whiteColor};
        }

        ${ScAnimatedPlaceholder} {
          color: ${({ theme }) => theme.whiteColor};
        }
      }
      .arrow {
        & > button {
          background-color: ${({ theme, selected }) =>
            selected ? theme.secondaryColor : theme.secondaryColor2};
        }

        path {
          fill: ${({ theme }) => theme.whiteColor};
          fill-opacity: 1;
          will-change: fill, fill-opacity;
        }

        @media (${device.mobile}) {
          path {
            fill: ${({ theme }) => theme.secondaryColor};
          }
        }
      }
    }
  }
`;

export type ScOptionsProps = ScSelectMainProps;

export const ScOptions = styled.div<ScOptionsProps>`
  position: absolute;
  left: 50%;
  right: 0;
  transform: translateX(-50%);
  background-color: ${({ theme, withBorderArrow, lightBg }) =>
    withBorderArrow
      ? theme.secondaryColor3
      : lightBg
        ? theme.grayColor5
        : theme.grayColor4};
  border-radius: 4px;
  overflow: hidden;
  width: ${({ withBorderMode }) =>
    withBorderMode ? '100%' : 'calc(100% - 8px)'};
  box-shadow: ${({ dropBoxShadow }) =>
    dropBoxShadow
      ? '0px 0px 10px rgba(0, 0, 0, 0.5), 0px 10px 30px rgba(0, 0, 0, 0.5)'
      : 'none'};

  & > .content {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  ${({ small, up, withBorderMode }) => {
    if (withBorderMode) return;
    return up
      ? css`
          bottom: 0;
          padding-bottom: ${small == null ? '48px' : '36px'};
        `
      : css`
          top: 0;
          padding-top: ${small == null ? '48px' : '36px'};
        `;
  }}

  ${({ withBorderMode }) =>
    withBorderMode &&
    css`
      top: 56px;
      border-radius: 12px;
      padding: 8px;

      & > .content {
        z-index: 2;
        position: relative;
      }

      & > .border {
        position: absolute;
        z-index: 1;
        inset: 0;
        border-radius: 12px;
        padding: 2px;
        -webkit-mask: linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;

        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          background: ${({ theme }) => theme.greenMarineGradient};
        }
      }
    `}

    ${({ lightColoredDropMode }) =>
      lightColoredDropMode &&
      css`
      --radius: 12px;
      border-radius: var(--radius);
      width: 100%;
      padding-bottom: 0;
      bottom: 56px !important;
      padding: 2px;
      z-index: 1;
      &:before {
        content: '';
        position: absolute;
        inset: 0;
        background: ${({ theme }) => theme.greenMarineGradient};
        z-index: -1;
      }
      & .content {
        background: ${({ theme }) => theme.grayColor3};
        border-radius: var(--radius);
      }
    `}

 ${({ paddingTop }) =>
   paddingTop &&
   css`
      padding-top: 36px;
    `}

	.ScrollbarsCustom {
    .scrollbarsWrapper {
      inset: 0 !important;
    }

    .trackY {
      width: 4px !important;
      border-radius: 2px !important;
      background: none !important;
      top: 6px !important;
      height: calc(100% - 12px) !important;

      & > div {
        width: 4px !important;
        background: ${({ theme }) => theme.grayColor6} !important;
        border-radius: 2px !important;
        transition: 0.3s;

        @media (hover: hover) {
          &.dragging {
            background: ${({ theme }) => theme.grayColor1} !important;
          }

          &:hover {
            background: ${({ theme }) => theme.grayColor1} !important;
          }
        }
      }
    }
  }

  .options-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    list-style: none;
    padding: ${({ withBorderMode }) =>
      withBorderMode ? '6px 8px 6px 0' : '8px 8px 8px 4px'};

    & > li {
      width: 100%;
      padding: 4px 8px;
      cursor: pointer;
      background-color: transparent;
      display: flex;
      align-items: center;
      border-radius: 8px;
      color: ${({ theme, withBorderArrow }) =>
        withBorderArrow ? theme.secondaryColor : theme.grayColor1};
      &.not-found {
        text-align: center;
        font-size: 12px;
      }

      &.with-icon {
        gap: 8px;

        img {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          object-fit: contain;
        }
      }

      & > .name {
        transition: color 0.2s;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        font-size: 16px;
        line-height: 20px;
      }

      &.active {
        color: ${({ theme }) => theme.whiteColor};
        font-weight: 700;
      }

      &.active,
      &:hover {
        ${({ withBorderMode }) =>
          withBorderMode &&
          css`
            background: ${({ theme }) => theme.grayColor5};
            border-radius: 8px;
            font-size: 14px;
            line-height: 20px;
          `}
      }
    }
  }

  .content > .addons {
    & > button {
      min-width: 100%;
    }
  }
`;

export const ScCheckboxBlock = styled.div<{ isVisibleTooltip?: boolean }>`
  font-family: ${({ theme }) => theme.mainFontFamily};
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;

  label {
    font-size: 12px;
    line-height: 18px;
    color: ${({ theme }) => theme.whiteColor};
    display: flex;
    gap: 4px;
    display: flex;
    gap: 12px;
    align-items: center;

    a {
      text-decoration: underline;
      text-underline-offset: 2px;
      text-decoration-thickness: 1px;
    }
  }

  .tooltip {
    z-index: 2;
    width: 20px;
    height: 20px;
    position: relative;
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    background-color: ${({ theme }) => hexToRGB(theme.whiteColor, 0.2)};
    border-radius: 6px;

    & > .tooltip-content {
      position: absolute;
      border-radius: 12px;
      left: 50%;
      transform: translateX(-50%);
      bottom: 28px;
      text-align: center;
      background: ${({ theme }) => theme.grayColor6};
      color: ${({ theme }) => theme.whiteColor};
      box-shadow: ${({ theme }) => `0px 0px 10px 0px
          ${hexToRGB(theme.blackColor, 0.3)},
        0px 10px 30px 0px ${hexToRGB(theme.blackColor, 0.3)}`};
      display: flex;
      width: 240px;
      padding: 8px;
      justify-content: center;
      align-items: center;

      @media (${device.mobileS}) {
        width: 200px;
        left: auto;
        right: 0;
        transform: translateX(0);
      }
    }

    ${({ isVisibleTooltip: isActive }) =>
      isActive &&
      css`
        svg path {
          fill-opacity: 1;
        }
      `}
  }
`;

const checkAnimation = keyframes`
  0%,100%{
    transform: scale(1);
  }
  50%{
    transform: scale(1.1);
  }
`;

export interface ScCheckboxProps {
  withColorBg?: boolean;
  width?: number;
  height?: number;
}

export const ScCheckbox = styled.div<ScCheckboxProps>`
  width: ${({ width }) => (width ? `${width}px` : '20px')};
  height: ${({ height }) => (height ? `${height}px` : '20px')};
  min-width: 20px;
  display: block;

  .checkbox {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    background: ${({ theme, withColorBg }) =>
      withColorBg
        ? hexToRGB(theme.whiteColor, 0.4)
        : hexToRGB(theme.whiteColor, 0.2)};
    border-radius: 4px;
    position: relative;
    animation: none;
    cursor: pointer;

    .border {
      position: absolute;
      z-index: 1;
      inset: 0;
      border-radius: 4px;
      padding: 2px;
      -webkit-mask: linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        background: ${({ theme }) => hexToRGB(theme.whiteColor, 0.2)};
      }
    }

    @media (hover: hover) {
      &:hover {
        background: ${({ theme }) => hexToRGB(theme.whiteColor, 0.6)};

        .border::before {
          background: ${({ theme }) => hexToRGB(theme.whiteColor, 0.6)};
        }
      }
    }
  }

  input {
    display: none !important;
  }

  input:checked ~ .checkbox {
    background: linear-gradient(
      90deg,
      rgba(0, 184, 62, 0.2) 0%,
      rgba(0, 182, 125, 0.2) 100%
    );
    animation: ${checkAnimation} 0.4s linear forwards;

    &:after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 50%;
      height: 50%;
      background: ${({ theme }) => theme.greenMarineGradient};
      border-radius: 2px;
    }

    .border::before {
      background: ${({ theme }) => theme.greenMarineGradient};
    }

    @media (hover: hover) {
      &:hover {
        background: linear-gradient(
          90deg,
          rgba(0, 184, 62, 0.6) 0%,
          rgba(0, 182, 125, 0.6) 100%
        );
      }
    }
  }

  input:disabled ~ .checkbox {
    cursor: default;
    opacity: 0.4;
    pointer-events: none;
  }
`;

export const ScTextArea = styled.textarea<ScInputProps>`
  position: relative;
  resize: none;
  height: 100%;
  width: calc(100% - 4px);
  border-radius: 12px;
  padding: ${({ icon }) => (icon ? '12px 16px 12px 36px' : '10px 16px')};
  font-size: 16px;
  line-height: 28px;
  color: ${({ theme }) => theme.whiteColor};
  background-color: transparent;
  transition: padding 0.2s, border-color 0.3s;
  appearance: none;
  outline: none;
  border: none;
  z-index: 2;

  & ~ ${ScAnimatedPlaceholder} {
    top: 10px;
    transform: none;
    left: 16px;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:focus,
  &:focus,
  &.filled {
    padding-top: 0;
    height: calc(100% - 24px);
    margin-top: 18px;
    margin-bottom: 2px;
    & ~ ${ScAnimatedPlaceholder} {
      font-size: 10px;
      line-height: 14px;
      top: 4px;
      height: 16px;
    }
  }

  &::placeholder {
    color: ${({ theme }) => theme.grayColor1};
  }

  &:disabled {
    color: ${({ theme }) => theme.grayColor6};
  }
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.grayColor3};
    border-radius: 2px;
    cursor: pointer;
    background-clip: padding-box;
  }
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

export const ScSelectSearch = styled.div`
  padding: 12px 8px 0 8px;

  & input {
    width: 100%;
    height: 20px;
    background: transparent;
    border: transparent;
    font-family: ${({ theme }) => theme.mainFontFamily};
    color: ${({ theme }) => theme.whiteColor};
    -webkit-text-fill-color: initial;
    appearance: none;
    outline: none;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400px;

    &::placeholder {
      color: ${({ theme }) => theme.grayColor1};
    }
  }

  & .divider {
    height: 2px;
    background-color: ${({ theme }) => theme.grayColor6};
    margin: 4px auto 0;
    border-radius: 1px;
  }
`;
